import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteStopServiceMutation,
	useGetStopServiceDetailQuery,
} from '@/api/forms-management/stopService';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { showLabel } from '@/utils/helper';
import { ZH_YESNO } from '@/common/constants/common';
import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetConsentServiceTerminationGenerateReportMutation,
	useGetExportCustomerPDFMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function StopServiceDetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, stopServiceId, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteStopService] = useDeleteStopServiceMutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getConsentServiceTerminationGenerateReport] =
		useGetConsentServiceTerminationGenerateReportMutation();

	const [serveExpanded, setServeExpanded] = useState(true);

	const { data: details }: any = useGetStopServiceDetailQuery(
		stopServiceId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.stopService'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteStopService(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	const formartReasonsForWithdrawal = () => {
		const arr = [];

		if (details?.data.serviceTargetReached)
			arr.push(
				t('preAdmission.serviceTargetReachedNoSpecify') +
					`(${details?.data.serviceTargetReachedSpecify})`
			);
		if (details?.data.admittedToResidential)
			arr.push(t('preAdmission.zh_admittedToResidential'));
		if (details?.data.admittedToPsychiatricHospital)
			arr.push(t('preAdmission.zh_admittedToPsychiatricHospital'));
		if (details?.data.persistentlyUnmotivated)
			arr.push(t('preAdmission.zh_persistentlyUnmotivated'));
		if (details?.data.selfWithdrawal)
			arr.push(t('preAdmission.zh_selfWithdrawal'));
		if (details?.data.untraceable) arr.push(t('preAdmission.zh_untraceable'));
		if (details?.data.serviceTerminationOthers)
			arr.push(
				t('preAdmission.serviceTerminationOthersNoSpecify') +
					`(${details?.data.serviceTerminationOthersSpecify})`
			);

		return arr.join(';\n');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.stopServiceDetail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response =
									await getConsentServiceTerminationGenerateReport(
										details?.data.id
									);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Box
						display='flex'
						justifyContent='space-between'
						marginBottom={2}
						onClick={() => setServeExpanded(!serveExpanded)}
					>
						<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
							{t('preAdmission.serviceUserInformation')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: serveExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>
					<Collapse in={serveExpanded}>
						<Grid container spacing={3} marginTop={0}>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_fileNo')}
									value={details?.data.fileNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.serviceUserName')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.dateOfTwcMembershipStart')}
									value={
										details?.data.dateOfTwcMembershipStart
											? dayjs(details?.data.dateOfTwcMembershipStart)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_dateOfTermination')}
									value={
										details?.data.dateOfTermination
											? dayjs(details?.data.dateOfTermination)
													.locale('en')
													.format(DISPLAY_DATE_FORMAT)
											: ''
									}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_isCase')}
									value={showLabel(details?.data.isCase, ZH_YESNO)}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('preAdmission.zh_isMember')}
									value={showLabel(details?.data.isMember, ZH_YESNO)}
								/>
							</Grid>
						</Grid>
					</Collapse>

					<Divider sx={{ margin: '24px 0' }} />
					<Grid container spacing={3} marginTop={0}>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.reasonsForWithdrawal')}
								value={formartReasonsForWithdrawal()}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Grid container spacing={3} marginTop={0}>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.zh_remarks')}
								value={details?.data.remarks}
							/>
						</Grid>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.serviceUserName')}
								value={details?.data.nameZh}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.workerName')}
								value={details?.data.workerName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.signatureDate')}
								value={
									details?.data.signatureDate
										? dayjs(details?.data.signatureDate)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
					</Grid>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
